import { PAID_FEATURES } from '@/utils/acl';
export default class NavigationItem {
  constructor(obj) {

    this.activeIcon = obj.activeIcon;
    this.color = obj.color;
    this.dashboardOrder = obj.dashboardOrder;
    this.destination = obj.destination;
    this.dynamic = obj.dynamic;
    this.id = obj.id;
    this.icon = obj.icon;
    this.name = obj.name;
    this.navigationGroupId = obj.navigationGroupId;
    this.order = obj.order;
    this.query = this.generateQuery();
    this.resourceId = obj.resourceId;
    this.resourceType = obj.resourceType;
    this.route = this.generateRoute();
    this.showOnDashboard = obj.showOnDashboard;
    this.unlockedBy = obj.unlockedBy;
    this.webIcon = obj.webIcon;

    // Psuedo private attributes
    this._cachedAccessibleItemsCount = null;
    this._cachedTotalItemsCount = null;
    this._calculatedAccessibleItems = 0;
    this._calculatedTotalItems = 0;

  }

  matchesRouteQuery(route) {
    if (route.query && route.query.nav) {
      // take string in format of '12-item,5-test' and turn it into ['12', '5']
      const ids = route.query.nav.split(',').map(q => q.split('-')[0]);
      // see if nav item id matches any
      return ids.find(id => id === `${this.id}`);
    }
  }

  generateQuery() {
    if (this.destination === 'home') {
      return  '';
    } else {
      // takes nav item like 'Practice Questions' and turn it into '12-practice-questions'
      let str = this.name.toLowerCase().split(' ');
      str.unshift(this.id);
      // replace is needed to strip out non alphanumeric characters
      return str.join('-').replace(/[^a-zA-Z0-9-_]/g, '');
    }
  }

  generateRoute() {
    const map = this.constructor.MAP()[this.destination];

    if (map) {
      let params = {};
      if (map.params){
        params = map.params;
      } else if (map.param) {
        params = { [map.param] : this.resourceId };
      }

      const routeQuery = map.query || {};

      return {
        name: map.name,
        params: params,
        query: this.query ? { ...routeQuery, nav: this.query } : routeQuery,
      };
    }
  }

  // accepts store and authorization plugin
  accessibleItemsCount(store, access) {
    if (isNaN(this._cachedAccessibleItemsCount) || this._calculatedAccessibleItems < store.getters['user/lastFetchedAt']) {
      this._calculatedAccessibleItems = Date.now();
      let categories;

      switch(this.destination) {
        case 'navigation_group':
          this._cachedAccessibleItemsCount = store.getters['navigationGroups/navigationGroups'][this.resourceId].accessibleItemsCount(store, access);
          break;
        case 'quiz_categories':
          categories = store.getters['categories/leaves'].filter(c => c.categoryTypeId === this.resourceId && access(store, 'category', c.id));
          this._cachedAccessibleItemsCount = categories.length;
          break;
        case 'attachment_categories':
        case 'attachments':
          store.getters['session/ajax'].get('/attachments/accessible_count', { params: { categoryTypeId: this.resourceId }}).then(resp => {
            const count = resp.data?.count;
            this._cachedAccessibleItemsCount = count || 0;
          });
          break;
        case 'flashcard_categories':
        case 'term_categories':
        case 'topic_categories':
        case 'topics':
          categories = store.getters['categories/leaves'].filter(c => c.categoryTypeId === this.resourceId && access(store, 'category', c.id));
          this._cachedAccessibleItemsCount = categories.reduce((a, b) => a + b.counts.total, 0);
          break;
      }
    }

    return this._cachedAccessibleItemsCount;
  }

  // accepts store
  totalItemsCount(store) {
    if (isNaN(this._cachedTotalItemsCount) || this._calculatedTotalItems < store.getters['user/lastFetchedAt']) {
      this._calculatedTotalItems = Date.now();
      let categories;

      switch(this.destination) {
        case 'navigation_group':
          this._cachedTotalItemsCount = store.getters['navigationGroups/navigationGroups'][this.resourceId].totalItemsCount(store);
          break;
        case 'quiz_categories':
          categories = store.getters['categories/leaves'].filter(c => c.categoryTypeId === this.resourceId);
          this._cachedTotalItemsCount = categories.length;
          break;
        case 'attachment_categories':
        case 'attachments':
        case 'flashcard_categories':
        case 'term_categories':
        case 'topic_categories':
        case 'topics':
          categories = store.getters['categories/leaves'].filter(c => c.categoryTypeId === this.resourceId);
          this._cachedTotalItemsCount = categories.reduce((a, b) => a + b.counts.total, 0);
          break;
      }
    }

    return this._cachedTotalItemsCount;
  }

  showFreeLabel(store, access) {
    const accessible = this.accessibleItemsCount(store, access);
    const total = this.totalItemsCount(store);

    return accessible !== total && accessible !== 0;
  }

  // note - right now this is just used for showing a lock button
  // may need revamped before we use it to trigger actual access control
  locked(store, access) {
    if (this.destination === 'custom_quiz') {
      return !access(store, 'feature', PAID_FEATURES.CUSTOM_QUIZZES);
    }
    return this.accessibleItemsCount(store, access) === 0 && this.totalItemsCount(store) !== 0;
  }

  static MAP() {
    return {
      about: {
        name: 'navigationGroups#show',
      },
      attachment_categories: {
        name: 'attachments#index',
      },
      attachments: {
        name: 'attachments#index',
      },
      bookmarks: {
        name: 'bookmarks#index',
      },
      card: {
        name: 'decks#show',
        params: {
          deckId: 'all',
        },
        param: 'cardId',
      },
      catalog: {
        name: 'catalog#index',
      },
      catalog_page: {
        name: 'catalog#page#show',
        param: 'catalogPageId',
      },
      custom_quiz: {
        name: 'quizzes#index',
      },
      dashboard: {
        name: 'dashboard#show',
        param: 'dashboardId',
      },
      dashboard_group: {
        name: 'dashboard#group',
        param: 'navigationGroupId',
      },
      deck: {
        name: 'decks#show',
        param: 'deckId',
      },
      discussions: {
        name: 'discussions#index',
      },
      flashcard_category: {
        name: 'categories#flashcard',
        param: 'categoryId',
      },
      flashcard_categories: {
        name: 'categories#index',
      },
      flashcard_quickstart: {
        name: 'categories#flashcard',
        params: { categoryId: 'all' },
      },
      grouping_categories: {
        name: 'groupings#index',
      },
      groupings: {
        name: 'groupings#index',
      },
      home: {
        name: 'home',
      },
      interactive_case_study: {
        name: 'interactiveCaseStudies#show',
      },
      interactive_case_studies: {
        name: 'interactiveCaseStudies#index',
      },
      learning_module: {
        name: 'learningModules#start',
        param: 'learningModuleId',
      },
      learning_modules: {
        name: 'learningModules#index',
      },
      library: {
        name: 'library#index',
      },
      mnemonic_categories: {
        name: 'mnemonics#index',
      },
      mnemonics: {
        name: 'mnemonics#index',
      },
      navigation_group: {
        name: 'navigationGroups#show',
      },
      progress_grouping: {
        name: 'progress',
      },
      progress: {
        name: 'progress',
      },
      quiz_categories: {
        name: 'quizzes#index',
      },
      scenario_categories: {
        name: 'scenario#sub#category',
      },
      search: {
        name: 'search#index',
      },
      term_categories: {
        name: 'categories#index',
      },
      test_simulations: {
        name: 'testSimulations#index',
      },
      topic_categories: {
        name: 'topics#index',
      },
      topics: {
        name: 'topics#index',
      },
      upgrade: {
        name: 'home',
        query: { upgrade: true },
      },
      user_account: {
        name: 'profile',
      },
      user_certification: {
        name: 'userCertifications#show',
      },
      user_certifications: {
        name: 'userCertifications#index',
      },
      web_page: {
        name: 'webPages#single',
      },
      web_page_categories: {
        name: 'webPages#index',
      },
      wux_test_simulations: {
        name: 'sims#index',
      },
    };
  }
}
